import Avatar from '-/Custom/Avatar';
import SearchBar from '-/Custom/SearchBar';
import type { EventEditFormValues } from '-/Event/EventEdit.types';
import { useEffect } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useQuery } from 'urql';
import { graphql } from '@/gql/graphql';
import type { TeamBoolExp } from '@/gql/scalars';
import useDebounce from '@/hooks/useDebounce';
import {
  DEFAULT_SEARCH_MINIMUM,
  MAXIMUM_EVENT_TEAM_COUNT,
} from '@/utils/constants';
import type { EntityListField } from '../Entity/EntityList.types';
import Box from '../UI/Box';
import Button from '../UI/Button';

const SEARCH_LIMIT = 5;

const GetEventEditTeamSearch = graphql(/* GraphQL */ `
  query GetEventEditTeamSearch($where: team_bool_exp!, $limit: Int!) {
    team(where: $where, limit: $limit, order_by: { title: asc }) {
      id
      title
      avatarId: avatar_id
      color: home_primary_color
    }
  }
`);

const GetEventEditTeamSearchRootParent = graphql(/* GraphQL */ `
  query GetEventEditTeamSearchRootParent($eventId: uuid!) {
    event_by_pk(id: $eventId) {
      id
      rootParentId: root_parent_id
    }
  }
`);

function EventEditTeamSearch() {
  const { control } = useFormContext<EventEditFormValues>();
  const parentId = useWatch({ name: 'parentId', control });
  const teams = useWatch({ name: 'teams', control });
  const { update } = useFieldArray({ name: 'teams', control });
  const { value, debouncedValue, onChangeText, reset } = useDebounce();

  // biome-ignore lint/correctness/useExhaustiveDependencies: parentId change should clear search
  useEffect(() => {
    reset();
  }, [parentId]);

  const [{ data: rootParentData }] = useQuery({
    query: GetEventEditTeamSearchRootParent,
    variables: {
      eventId: parentId || '',
    },
    pause: !parentId,
  });

  const rootParentId = parentId
    ? rootParentData?.event_by_pk?.rootParentId
    : null;

  const onPressTeam = (team: EntityListField) => {
    const index = teams?.findIndex((formTeam) => !formTeam.title);

    if (index !== undefined && index !== -1) {
      update(index, team);
    }
  };

  const variables: {
    where: TeamBoolExp;
    limit: number;
  } = (() => {
    if (debouncedValue.length >= DEFAULT_SEARCH_MINIMUM) {
      return {
        where: {
          title: {
            _ilike: `%${debouncedValue}%`,
          },
          _or: [
            {
              on_team: {
                _eq: true,
              },
            },
            ...(rootParentId
              ? [
                  {
                    root_event_id: {
                      _eq: rootParentId,
                    },
                  },
                ]
              : []),
          ],
        },
        limit: SEARCH_LIMIT,
      };
    }

    if (parentId) {
      return {
        where: {
          team_events: {
            event_id: {
              _eq: parentId,
            },
          },
        },
        limit: MAXIMUM_EVENT_TEAM_COUNT,
      };
    }

    return {
      where: {
        on_team: {
          _eq: true,
        },
      },
      limit: SEARCH_LIMIT,
    };
  })();

  const [{ data }] = useQuery({
    query: GetEventEditTeamSearch,
    variables,
  });

  const teamsWithoutSelected = data?.team.filter(
    (team) => !teams?.find((formTeam) => formTeam.id === team.id),
  );

  return (
    <>
      <Box p="sm">
        <SearchBar
          value={value}
          onPressCancel={reset}
          onChangeText={onChangeText}
          placeholder="Search Existing Teams..."
        />
      </Box>

      {teamsWithoutSelected?.length ? (
        <Box direction="horizontal" space="xs" p="md" wrap>
          {teamsWithoutSelected.map((team) => (
            <Button
              onPress={() => onPressTeam(team)}
              key={team.id}
              size="sm"
              shape="pill"
              appearance="outline"
              variant="secondary"
              padding={0}
              paddingHorizontal={0}
              paddingRight={8}
              gap={2}
              testID="EventEditTeamSearchItem"
            >
              <Avatar
                id={team.avatarId || ''}
                name={team.title}
                size="sm"
                color={team.color}
              />
              <Button.Text>{team.title}</Button.Text>
            </Button>
          ))}
        </Box>
      ) : null}
    </>
  );
}

export default EventEditTeamSearch;
